import React, { useState } from "react";
import { Alert, Form, Button, Spinner } from "react-bootstrap";
import { connect } from "react-redux";
import { func, string, bool } from "prop-types";
import jwtDecode from "jwt-decode";
import FontAwesome from "react-fontawesome";
import circleSuccessIcon from "resources/images/circle-check-fill-dark-green.svg";
import errorIcon from "resources/images/warning-dark-red.png";
import ReactPasswordStrength from "react-password-strength";
import "!style-loader!css-loader!./passwordStyles.css";
import {
  setPassword,
  resendVerificationEmail,
  setNewPassword,
} from "../actions";

const SetPasswordForm = ({
  onSetPassword,
  verifyToken,
  tk1,
  tk2,
  onSetNewPassword,
  setPasswordLoading,
  setNewPasswordLoading,
}) => {
  const [newPasswordInput, setNewPasswordInput] = useState("");
  const [newPasswordValid, setNewPasswordValid] = useState(false);
  const [continueButtonClicked, setContinueButtonClicked] = useState(false);
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [validationStates, setValidationStates] = useState({
    newPassword: null,
    newPasswordConfirm: null,
    passwordsMatch: null,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  /* ----------- Validation Method ------------ */

  const getValidationStates = () => {
    setValidationStates({
      newPassword: newPasswordValid ? null : "error",
      newPasswordConfirm:
        newPasswordInput === newPasswordConfirm ? null : "error",
      passwordsMatch: newPasswordInput === newPasswordConfirm,
    });

    if (newPasswordInput !== newPasswordConfirm || !newPasswordValid) {
      setContinueButtonClicked(false);
    }
    return newPasswordInput === newPasswordConfirm && newPasswordValid;
  };

  const handleInput = (event) => {
    const { value } = event.target;

    setNewPasswordConfirm(value);
    setValidationStates((prevState) => ({
      ...prevState,
      newPasswordConfirm: newPasswordInput === value ? null : "error",
      passwordsMatch: newPasswordInput === value,
    }));
  };

  const handlePasswordInput = (data) => {
    const { password, isValid } = data;

    setNewPasswordInput(password);
    setNewPasswordValid(isValid);
    setValidationStates((prevState) => ({
      ...prevState,
      newPassword: !data.isValid ? "error" : null,
    }));
  };

  const handleSubmit = () => {
    setContinueButtonClicked(true);

    if (getValidationStates()) {
      if (tk1 && tk2) {
        // pass in the password reset tokens and the new password
        onSetNewPassword(tk1, tk2, newPasswordInput);
      } else if (verifyToken) {
        // pass in the verify token and the new password
        onSetPassword(verifyToken, newPasswordInput);
      }
    }
  };

  const handleKeyDown = (event) => {
    if (event.code === "Enter") {
      handleSubmit();
    }
  };

  const showMinimumPasswordError =
    newPasswordInput.length !== 0 && newPasswordInput.length < 8;

  const showNewPasswordError = validationStates.newPassword !== null;
  const showNewPasswordConfirmError =
    validationStates.newPasswordConfirm !== null;

  let emailAddress = "";
  if (tk1 && tk2) {
    const decodedToken = jwtDecode(tk2);
    emailAddress = decodedToken?.emailAddress;
  } else if (verifyToken) {
    const decodedVerifyToken = jwtDecode(verifyToken);
    emailAddress = decodedVerifyToken?.email;
  }

  let buttonText = "";
  if (tk1 && tk2) {
    buttonText = "Create Password & Login";
  } else if (verifyToken) {
    buttonText = "Continue";
  }

  const headerText = tk1 && tk2 ? "Create New Password" : "Welcome to Criteria";
  const helperText =
    tk1 && tk2
      ? "Enter your new password below."
      : "Create a password for your account:";

  const loadingSetPasswords = setPasswordLoading || setNewPasswordLoading;

  return (
    <>
      {verifyToken ? (
        <Alert
          variant="success"
          className="p-3"
          style={{
            backgroundColor: "#CCF2E1",
            border: "1px solid rgba(0, 192, 103, 0.20)",
            marginBottom: "32px",
          }}
        >
          <div className="d-flex" style={{ color: "#006034" }}>
            <img
              src={circleSuccessIcon}
              alt="success"
              className="mr-3 align-self-start"
            />
            Your email has been verified. Now, let’s set your password.
          </div>
        </Alert>
      ) : null}
      {showNewPasswordConfirmError && (
        <Alert
          variant="error"
          className="mb-4 p-3"
          style={{
            backgroundColor: "#FAE5EE",
            border: "1px solid #F7CCDD",
          }}
        >
          <div style={{ color: "#94003C" }}>
            <img
              src={errorIcon}
              alt="error"
              className="mr-3 align-self-start"
              height="16px"
              width="16px"
            />
            Passwords do not match
          </div>
        </Alert>
      )}
      <h5 className="mb-4" style={{ fontSize: "28px" }}>
        {headerText}
      </h5>
      <p className="mb-2" style={{ fontSize: "16px" }}>
        {helperText}
      </p>
      <div
        className="d-flex align-items-center mb-4"
        style={{
          border: "1px solid #A9AAAC",
          borderRadius: "4px",
          borderLeft: "6px solid #425CC7",
          padding: "12px 16px",
          fontSize: "16px",
          textDecoration: "underline",
        }}
      >
        {emailAddress}
      </div>
      <Form.Group className="mb-4">
        <Form.Label
          className="font-weight-bold"
          style={{
            fontSize: "14px",
            marginBottom: "2px",
            ...(showNewPasswordError ? { color: "#D50057" } : {}),
          }}
        >
          Password
        </Form.Label>
        <div style={{ position: "relative" }}>
          <ReactPasswordStrength
            minLength={8}
            inputProps={{
              id: "newPassword",
              type: showPassword ? "text" : "password",
            }}
            minScore={3}
            scoreWords={["weak", "okay", "good", "strong", "stronger"]}
            changeCallback={handlePasswordInput}
            style={{
              height: "34px",
              borderRadius: "4px",
              marginBottom: "5px",
              paddingRight: "40px",
              fontFamily: "Lato",
              ...(showNewPasswordError ? { border: "1px solid #D50057" } : {}),
            }}
          />
          <FontAwesome
            name={showPassword ? "eye-slash" : "eye"}
            onClick={() => setShowPassword(!showPassword)}
            color="#A9AAAC"
            style={{
              color: "#A9AAAC",
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
              height: "16px",
              width: "16px",
            }}
            size="lg"
          />
        </div>
        {showMinimumPasswordError ? (
          <p
            className="manual-override-invalid-feedback"
            style={{ fontSize: "12px" }}
          >
            Minimum password length is 8 characters.
          </p>
        ) : null}
        {showNewPasswordError && !showMinimumPasswordError ? (
          <p
            className="manual-override-invalid-feedback"
            style={{ fontSize: "12px" }}
          >
            Please choose a stronger password. Try using uncommon words,
            non-standard uPPercasing, creative spelllling, personal slang words,
            and non-obvious numbers and symbols.
          </p>
        ) : null}
      </Form.Group>
      <Form.Group className="mb-4">
        <Form.Label
          className="font-weight-bold"
          style={{
            fontSize: "14px",
            marginBottom: "2px",
            ...(showNewPasswordConfirmError ? { color: "#D50057" } : {}),
          }}
        >
          Confirm Password
        </Form.Label>
        <div style={{ position: "relative" }}>
          <Form.Control
            type={showPasswordConfirm ? "text" : "password"}
            id="newPasswordConfirm"
            value={newPasswordConfirm}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            style={{
              fontSize: "13px",
              color: "#000000",
              ...(showNewPasswordConfirmError
                ? { border: "1px solid #D50057" }
                : {}),
            }}
          />
          <FontAwesome
            name={showPasswordConfirm ? "eye-slash" : "eye"}
            onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
            color="#A9AAAC"
            style={{
              color: "#A9AAAC",
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
              height: "16px",
              width: "16px",
            }}
            size="lg"
          />
        </div>
      </Form.Group>
      <div className="text-center">
        <Button
          variant="primary"
          size="lg"
          onClick={handleSubmit}
          id="setPassword"
          style={{
            width: "100%",
            padding: "6px 12px",
            fontSize: "14px",
            lineHeight: "20px",
          }}
          className={`font-weight-bold ${loadingSetPasswords ? "disabled-loading" : ""}`}
          disabled={continueButtonClicked || !newPasswordValid}
        >
          <span style={{ marginRight: "6px" }}>
            {loadingSetPasswords ? "Logging into Criteria" : buttonText}
          </span>
          {loadingSetPasswords && (
            <Spinner
              animation="border"
              role="status"
              style={{
                color: "#FFFFFF",
                height: "18px",
                width: "18px",
              }}
            ></Spinner>
          )}
        </Button>
      </div>
    </>
  );
};

SetPasswordForm.propTypes = {
  onSetPassword: func,
  verifyToken: string,
  tk1: string,
  tk2: string,
  onSetNewPassword: func,
  setPasswordLoading: bool,
  setNewPasswordLoading: bool,
};

const mapStateToProps = (state) => ({
  setPasswordLoading: state.get("App").get("setPasswordLoading"),
  setNewPasswordLoading: state.get("App").get("setNewPasswordLoading"),
});

function mapDispatchToProps(dispatch) {
  return {
    onSetPassword: (verifyToken, password) =>
      dispatch(setPassword(verifyToken, password)),
    onSetNewPassword: (tk1, tk2, password) =>
      dispatch(setNewPassword(tk1, tk2, password)),
    onResendVerificationEmail: (emailAddress) =>
      dispatch(resendVerificationEmail(emailAddress)),
    dispatch,
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(SetPasswordForm);
